import { JournalManifest, JournalPermissions, JournalTemplate } from '../types/journal';

/**
 * A refusal because a right, the company's policy or Journal itself changed since the pane read
 * them: the buttons are stale, so the pane reads the rights again.
 */
export function isRightsRefusal(code: string | undefined): boolean {
  return code === 'JOURNAL_PUSH_DENIED' || code === 'JOURNAL_POST_DENIED' || code === 'JOURNAL_POST_POLICY_DENIED'
    || code === 'JOURNAL_DISABLED';
}

/**
 * "Finalize & post" sends the draft and posts it, so it needs both grants; push alone saves a draft.
 * Without a draft stage there is nothing to post, and a draft-only company posts in the ERP.
 */
export function canFinalizeAndPost(perms: JournalPermissions): boolean {
  return perms.canPush && perms.canPost && perms.erpHasDraftState === true && perms.companyAllowedOperations !== 'draft_only';
}

/**
 * Whether the signed-in user prepared the sheet's journal or sent it for review, on a template that
 * requires review: the backend refuses them its send, so the pane greys Push and Finalize & post out.
 */
export function ownJournalUnderReview(
  template: Pick<JournalTemplate, 'approvalRequired'> | null,
  manifest: Pick<JournalManifest, 'preparedBy' | 'submittedBy'> | undefined,
  userId: string | undefined,
): boolean {
  if (template?.approvalRequired !== true || !manifest || !userId) return false;
  return manifest.preparedBy === userId || manifest.submittedBy === userId;
}

/**
 * Explains an action the user cannot take, so a hidden button leaves a reason
 * rather than a silent gap. Empty when the user has everything.
 *
 * Push and post are separate grants: someone may legitimately prepare and check
 * a journal without being the person who sends it, which is why lacking both
 * still leaves a usable tab.
 */
export function journalPermissionNote(perms: JournalPermissions, approvalRequired = false): string {
  // Applies to everyone, push access or not: on such a template nobody sends
  // their own journal.
  if (approvalRequired) {
    return 'This template requires review. Send the journal for review, and a colleague with push access sends it to your ERP. Nobody can send a journal they prepared or sent for review.';
  }
  if (!perms.canPush) {
    return 'You can prepare and check journals. Sending them to your ERP needs push access — ask your administrator.';
  }
  // Where the ERP has no draft stage, sending IS posting. Saying "posting is
  // done in the ERP" there tells the user their entry is still waiting when it
  // is already in the ledger — the most dangerous thing this pane can say.
  //
  // Unknown counts as no draft stage, matching the backend's own default. An
  // unnecessary warning is a nuisance; a missing one is a posted ledger entry
  // the user thought was a draft.
  if (perms.erpHasDraftState !== true) {
    return 'Journals post to your ERP as soon as you send them. They cannot be deleted afterwards — correct a mistake with a reversing entry.';
  }
  if (!perms.canPost) {
    // draft_only is a company-wide policy, not a gap in this user's access.
    return perms.companyAllowedOperations === 'draft_only'
      ? 'Journals are sent to your ERP as drafts. Posting them is done in the ERP.'
      : 'You can send drafts. Posting them, and Finalize & post, also need post access — ask your administrator.';
  }
  return '';
}
